<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-body p-20-px flex-grow-1 d-flex flex-column overflow-y-auto">
      <section
        v-for="group in AccessGroups"
        :key="group.title"
        class="access-group"
      >
        <h3 class="access-group__title">
          {{ group.title }}
        </h3>
        <div
          v-for="item in group.items"
          :key="item.key"
          class="access-item"
        >
          <div class="access-row">
            <div class="access-row__label">
              {{ item.label }}
            </div>
            <a-switch
              v-model="form[item.key]"
              :disabled="isSaving"
              @change="onSwitchChange(item, $event)"
            />
          </div>

          <div
            v-if="item.children && form[item.key]"
            class="access-dependents"
          >
            <div
              v-for="child in item.children"
              :key="child.key"
              class="access-row"
            >
              <div class="access-row__label">
                {{ child.label }}
              </div>
              <a-switch
                v-model="form[child.key]"
                :disabled="isSaving"
              />
            </div>
            <a-button
              v-if="item.key === 'isEnableAlertTriage' && isCloudsineStaff"
              class="mt-2 mb-3"
              @click="aiTriageModelConfigVisible = true"
            >
              Configure AI models
            </a-button>
          </div>

          <div
            v-if="item.key === 'isEnableSMS' && form.isEnableSMS"
            class="access-dependents"
          >
            <div class="access-row">
              <label
                class="access-row__label"
                for="access-sms-prefix"
              >
                SMS Region
              </label>
              <a-input
                id="access-sms-prefix"
                v-model="form.smsPrefix"
                class="access-sms-prefix"
                placeholder="SMS region"
                :disabled="isSaving"
              />
            </div>
          </div>

          <div
            v-if="item.key === 'isEnableChangeEvents' && form.isEnableChangeEvents"
            class="access-dependents"
          >
            <a-card
              size="small"
              title="Change Events Maintenance"
              class="mb-3"
            >
              <!-- The buttons work only once Change Events is saved as
                   enabled for this account. The switch above is form
                   state; a backfill against an account whose saved
                   access is still off must not be possible. -->
              <a-alert
                v-if="!changeEventsSaved"
                type="info"
                show-icon
                class="mb-3"
                message="Save your changes first. Backfill and rollup can be requested once Change Events is saved as enabled for this account."
              />
              <a-alert
                v-else-if="jobs.error"
                type="error"
                show-icon
                class="mb-3"
                message="The maintenance requests could not be loaded. Backfill and rollup cannot be requested until they are."
              />
              <a-alert
                v-else-if="jobs.installed === false"
                type="warning"
                show-icon
                class="mb-3"
                message="The job request table is not installed on this environment. Backfill and rollup cannot be requested here."
              />
              <a-alert
                v-else-if="jobs.serviceAlive === false"
                type="warning"
                show-icon
                class="mb-3"
                message="The Change Events service has not reported in the last five minutes. A request will wait until it is running."
              />

              <p class="mb-3">
                <strong>Backfill</strong> groups this account's past alerts into change events and site-wide
                incidents for the selected date range, up to 90 days per request. Run it once when the feature
                is first enabled, so the dashboard has history from the start.
              </p>
              <a-row
                :gutter="[8, 8]"
                type="flex"
                align="middle"
                class="mb-3"
              >
                <a-col>
                  <a-range-picker
                    v-model="backfillRange"
                    :format="DATE_FORMAT.DDMMMYYYY"
                    :disabled-date="disabledBackfillDate"
                    :placeholder="['Start date', 'End date']"
                    @calendarChange="onBackfillCalendarChange"
                    @openChange="onBackfillOpenChange"
                  />
                </a-col>
                <a-col>
                  <a-button
                    type="primary"
                    :loading="requesting === 'backfill'"
                    :disabled="!canRequest || !backfillRangeValid"
                    @click="requestJob('backfill')"
                  >
                    Request Backfill
                  </a-button>
                </a-col>
                <a-col v-if="backfillRangeError">
                  <span class="backfill-range-error">
                    {{ backfillRangeError }}
                  </span>
                </a-col>
              </a-row>

              <p class="mb-3">
                <strong>Rollup</strong> rebuilds the file history the grouping uses to tell a genuine change
                from routine churn. It runs nightly on its own; request it here after a backfill so the new
                history is used immediately.
              </p>
              <a-row
                :gutter="[8, 8]"
                class="mb-3"
              >
                <a-col>
                  <a-button
                    :loading="requesting === 'rollup'"
                    :disabled="!canRequest"
                    @click="requestJob('rollup')"
                  >
                    Request Rollup
                  </a-button>
                </a-col>
              </a-row>

              <a-table
                :columns="jobColumns"
                :data-source="jobs.list"
                :loading="jobs.loading"
                :pagination="false"
                row-key="id"
                size="small"
                :locale="{ emptyText: 'No requests have been made for this account.' }"
              >
                <template
                  slot="mode"
                  slot-scope="mode"
                >
                  {{ mode === 'backfill' ? 'Backfill' : 'Rollup' }}
                </template>
                <template
                  slot="range"
                  slot-scope="_, job"
                >
                  <span v-if="job.mode === 'backfill'">
                    {{ formatDay(job.since) }} to {{ formatDay(job.until) }}
                  </span>
                  <span v-else>
                    -
                  </span>
                </template>
                <template
                  slot="status"
                  slot-scope="status, job"
                >
                  <a-tag :color="jobStatusColor(status)">
                    {{ jobStatusText(status) }}
                  </a-tag>
                  <a-tooltip
                    v-if="status === 'failed' && job.error"
                    :title="job.error"
                  >
                    <a-icon
                      type="info-circle"
                      class="ml-1"
                    />
                  </a-tooltip>
                </template>
                <template
                  slot="result"
                  slot-scope="_, job"
                >
                  <span v-if="job.status === 'done' && job.mode === 'backfill'">
                    {{ job.alerts_read }} alerts read, {{ job.events_touched }} events written
                  </span>
                  <span v-else-if="job.status === 'done'">
                    Completed
                  </span>
                  <span v-else>
                    -
                  </span>
                </template>
                <template
                  slot="requested_at"
                  slot-scope="value"
                >
                  {{ formatTime(value) }}
                </template>
                <template
                  slot="finished_at"
                  slot-scope="value"
                >
                  {{ formatTime(value) }}
                </template>
              </a-table>
            </a-card>
          </div>
        </div>
      </section>
    </div>
    <div class="a-footer">
      <a-button
        size="large"
        class="mr-3"
        :disabled="!isDirty || isSaving"
        @click="undo"
      >
        Undo
      </a-button>
      <a-button
        size="large"
        type="primary"
        :loading="isSaving"
        :disabled="!isDirty || isSaving"
        @click="save"
      >
        Save
      </a-button>
    </div>
    <AiTriageModelConfigModal
      v-if="isCloudsineStaff"
      :visible="aiTriageModelConfigVisible"
      :customerId="user.customerId"
      @close="aiTriageModelConfigVisible = false"
    />
  </div>
</template>

<script>
  import moment from 'moment-timezone';
  import { DATE_FORMAT } from '@constants/dateFormat';
  import { UserDate } from '@shared/services/userDate.service';
  import ChangeEventsService from '@modules/ChangeEvents/services/ChangeEvents.service';
  import dirtyFormMixin from '../../mixins/dirtyForm.mixin';
  import UserManagementService from '../../services/UserManagement.service';
  import { AccessGroups } from '../../consts/AccessGroups.const';

  // The most days one backfill may cover. The API enforces the same number; the
  // picker and the button enforce it here so the request cannot be refused.
  const BACKFILL_MAX_DAYS = 90;

  const SWITCH_KEYS = AccessGroups.flatMap(({ items }) => items.flatMap(({ key, children = [] }) => [key, ...children.map((child) => child.key)]));

  const toFormValues = (user) => SWITCH_KEYS.reduce((values, key) => ({ ...values, [key]: !!user[key] }), { smsPrefix: user.smsPrefix || '' });

  export default {
    name: 'AccessTab',
    components: {
      AiTriageModelConfigModal: () => import('../AiTriageModelConfigModal.vue')
    },
    mixins: [dirtyFormMixin],
    props: {
      user: { type: Object, required: true }
    },
    data() {
      return {
        AccessGroups,
        DATE_FORMAT,
        form: toFormValues({}),
        aiTriageModelConfigVisible: false,
        // Change Events maintenance: the recent backfill and rollup requests.
        jobs: { installed: null, serviceAlive: null, list: [], loading: false, error: false },
        jobsTimer: null,
        // Only the newest loadJobs() may write, so a reply that lands after the
        // feature was switched off cannot re-arm the polling.
        jobsToken: 0,
        backfillRange: [],
        // The start date while the end date is still being picked. v-model only
        // updates once both are chosen, and the cap needs the start before that.
        backfillPendingStart: null,
        requesting: '',
        jobColumns: [
          { title: 'Job', dataIndex: 'mode', scopedSlots: { customRender: 'mode' }, width: 90 },
          { title: 'Date Range', key: 'range', scopedSlots: { customRender: 'range' } },
          { title: 'Status', dataIndex: 'status', scopedSlots: { customRender: 'status' }, width: 120 },
          { title: 'Result', key: 'result', scopedSlots: { customRender: 'result' } },
          { title: 'Requested', dataIndex: 'requested_at', scopedSlots: { customRender: 'requested_at' }, width: 170 },
          { title: 'Finished', dataIndex: 'finished_at', scopedSlots: { customRender: 'finished_at' }, width: 170 }
        ]
      };
    },
    computed: {
      isCloudsineStaff() {
        return this.$store.getters.hasFeature('cloudsine-staff');
      },
      /** Change Events as SAVED for this account, not as the unsaved switch reads. */
      changeEventsSaved() {
        return Boolean(this.pristine && this.pristine.isEnableChangeEvents);
      },
      /** The maintenance requests are listed and polled only while the card is shown and its feature is saved as enabled. */
      isWatchingJobs() {
        return this.form.isEnableChangeEvents && this.changeEventsSaved;
      },
      canRequest() {
        return this.changeEventsSaved && this.jobs.installed === true && !this.jobs.error && !this.requesting;
      },
      /** Why the chosen backfill range cannot be sent, or an empty string. */
      backfillRangeError() {
        const [start, end] = this.backfillRange;
        if (!start || !end) return '';
        if (end.isBefore(start, 'day')) return 'The end date is before the start date.';
        // Inclusive on both ends, like the picker.
        if (end.diff(start, 'days') + 1 > BACKFILL_MAX_DAYS) {
          return `A backfill covers at most ${BACKFILL_MAX_DAYS} days. Choose a shorter range.`;
        }
        return '';
      },
      backfillRangeValid() {
        const [start, end] = this.backfillRange;
        return !!(start && end) && !this.backfillRangeError;
      }
    },
    watch: {
      isWatchingJobs(isWatching) {
        if (isWatching) {
          this.loadJobs();
        } else {
          this.stopJobsPolling();
        }
      },
      // Show what the server kept after a save, unless the person is already editing again.
      user() {
        if (!this.isDirty) {
          this.setFormValues(toFormValues(this.user));
          this.markPristine();
        }
      }
    },
    created() {
      this.setFormValues(toFormValues(this.user));
      this.markPristine();
    },
    beforeDestroy() {
      this.stopJobsPolling();
    },
    methods: {
      getFormValues() {
        return { ...this.form };
      },
      setFormValues(values) {
        Object.assign(this.form, values);
      },
      onSwitchChange(item, checked) {
        if (!checked && item.children) {
          item.children.forEach(({ key }) => {
            this.form[key] = false;
          });
        }
      },
      /**
       * The names the API takes on write are not the ones formatUser reads it
       * back under (e.g. enable_nlp_engine here, enable_engine_nlp there).
       */
      buildBody() {
        const {
          isEnableContentIntegrityEngine,
          isEnableDndSslMonitoring,
          isEnableImageEngine,
          isEnableNLPEngine,
          isEnableJavaScriptMalwareEngine,
          isEnableAlertTriage,
          isEnableFqdnAiTriagePrompt,
          isEnableFqdnAiTriagePreferences,
          isEnablePageRender,
          isEnableStatusCodeMonitoring,
          isEnableSMS,
          smsPrefix,
          isEnableFirecrawl,
          isEnableVideoChangeEngine,
          isEnableChangeEvents
        } = this.form;
        return {
          feature_access: {
            enable_content_integrity_engine: isEnableContentIntegrityEngine,
            enable_image_engine: isEnableImageEngine,
            enable_nlp_engine: isEnableNLPEngine,
            enable_static_malware_engine: isEnableJavaScriptMalwareEngine,
            enable_triage_alert: isEnableAlertTriage,
            enable_fqdn_ai_triage_prompt: isEnableAlertTriage ? isEnableFqdnAiTriagePrompt : false,
            enable_fqdn_ai_triage_preferences: isEnableAlertTriage ? isEnableFqdnAiTriagePreferences : false,
            enable_page_render: isEnablePageRender,
            enable_status_code_monitoring: isEnableStatusCodeMonitoring,
            enable_dns_whois_ssl_monitoring: isEnableDndSslMonitoring,
            enable_sms: isEnableSMS,
            sms_number_prefix: smsPrefix,
            enable_firecrawl: isEnableFirecrawl,
            enable_video_change_engine: isEnableVideoChangeEngine,
            enable_change_events: isEnableChangeEvents
          }
        };
      },
      async save() {
        this.isSaving = true;
        try {
          await UserManagementService.updateUserById(this.user.customerId, this.buildBody());
          this.$showSuccessMessage('Updated user');
          this.markPristine();
          this.$emit('saved');
        } catch (error) {
          this.$showMultiErrorMessage(error.messages);
        } finally {
          this.isSaving = false;
        }
      },
      async loadJobs() {
        const token = ++this.jobsToken;
        this.jobs.loading = true;
        try {
          const data = await ChangeEventsService.fetchJobs(this.user.customerId);
          if (token !== this.jobsToken) return;
          this.jobs.error = false;
          this.jobs.installed = !!data.installed;
          this.jobs.serviceAlive = data.installed ? !!data.service_alive : null;
          this.jobs.list = data.jobs || [];
        } catch (error) {
          if (token !== this.jobsToken) return;
          // Said out loud: a broken endpoint must not look like a healthy one
          // with no jobs.
          this.jobs.error = true;
          this.jobs.installed = null;
        } finally {
          if (token === this.jobsToken) this.jobs.loading = false;
        }
        // Keep watching while anything is still queued or running.
        this.stopJobsPolling();
        if (this.jobs.list.some((job) => job.status === 'queued' || job.status === 'running')) {
          this.jobsTimer = setTimeout(() => this.loadJobs(), 10000);
        }
      },
      stopJobsPolling() {
        // Also retires any reply still in flight, so it cannot re-arm the timer.
        this.jobsToken += 1;
        this.jobs.loading = false;
        if (this.jobsTimer) {
          clearTimeout(this.jobsTimer);
          this.jobsTimer = null;
        }
      },
      async requestJob(mode) {
        if (!this.canRequest) return;
        const body = { customer_id: this.user.customerId, mode };
        if (mode === 'backfill') {
          if (!this.backfillRangeValid) return;
          const [start, end] = this.backfillRange;
          body.since = start.format('YYYY-MM-DD');
          // The picker's end day is inclusive; the API's until is exclusive.
          body.until = end.clone().add(1, 'day').format('YYYY-MM-DD');
        }
        this.requesting = mode;
        try {
          await ChangeEventsService.requestJob(body);
          this.$showSuccessMessage(mode === 'backfill' ? 'Backfill requested. The service picks it up within one minute.' : 'Rollup requested. The service picks it up within one minute.');
          if (mode === 'backfill') this.backfillRange = [];
        } catch (error) {
          const message = error && error.response && error.response.data && error.response.data.message;
          this.$showErrorMessage(message || `The ${mode} could not be requested.`);
        } finally {
          this.requesting = '';
          this.loadJobs();
        }
      },
      onBackfillCalendarChange(dates) {
        this.backfillPendingStart = dates && dates.length === 1 ? dates[0] : null;
      },
      onBackfillOpenChange(open) {
        if (!open) this.backfillPendingStart = null;
      },
      /**
       * Nothing in the future, and, once a start day is chosen, nothing more
       * than BACKFILL_MAX_DAYS after it (both ends inclusive). How far back the
       * start may go is not limited: the API caps the span, not the age.
       */
      disabledBackfillDate(current) {
        if (!current) return false;
        if (current > moment().endOf('day')) return true;
        const start = this.backfillPendingStart;
        if (!start) return false;
        return current > start.clone().add(BACKFILL_MAX_DAYS - 1, 'day').endOf('day') || current < start.clone().subtract(BACKFILL_MAX_DAYS - 1, 'day').startOf('day');
      },
      // Shown in the account time zone, like every other API timestamp in the app.
      formatDay(value) {
        return value ? moment.tz(value, UserDate.userTimezone).format(DATE_FORMAT.DDMMMYYYY) : '-';
      },
      formatTime(value) {
        return value ? moment.tz(value, UserDate.userTimezone).format(DATE_FORMAT.DDMMMYYYYHHmm) : '-';
      },
      jobStatusText(status) {
        return { queued: 'Queued', running: 'Running', done: 'Completed', failed: 'Failed' }[status] || status;
      },
      jobStatusColor(status) {
        return { queued: 'blue', running: 'orange', done: 'green', failed: 'red' }[status] || '';
      }
    }
  };
</script>

<style lang="scss" scoped>
  .access-group {
    flex-shrink: 0;
    background: #ffffff;
    border: 1px solid #e3e7ea;
    border-radius: 8px;
    padding: 16px 18px;
    margin-bottom: 16px;

    &__title {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #8a94a0;
      margin: 0 0 10px;
    }
  }

  .access-item + .access-item {
    border-top: 1px solid #f0f0f0;
  }

  .access-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 0;

    &__label {
      margin: 0;
      font-weight: 600;
      color: #262626;
    }
  }

  .access-dependents {
    margin: 0 0 12px 4px;
    padding-left: 1.5rem;
    border-left: 2px solid #e8e8e8;
  }

  .access-sms-prefix {
    max-width: 240px;
  }

  .backfill-range-error {
    color: #f5222d;
    font-size: 12px;
  }
</style>
